/** Mentology Classroom key specification — client mirror of server/services/keyRegistry.js */

const MASTER_TEST_KEY = (
  (import.meta.env.VITE_MASTER_TESTING_KEY as string | undefined)?.trim() ?? 'M1-TEST-ME7'
).toUpperCase();

export const KEY_TYPES = {
  PAID_SUBSCRIPTION: 'PAID_SUBSCRIPTION',
  TEMP_ORG: 'TEMP_ORG',
  ORG_STUDENT: 'ORG_STUDENT',
} as const;

export type KeyType = (typeof KEY_TYPES)[keyof typeof KEY_TYPES];

export const REGEX = {
  PAID_PRIVATE: /^MPL-\d{4}$/,
  PAID_ORG: /^MPL-[A-Z]{3}-\d{4}$/,
  TEMP_ORG: /^MLPT-\d{4}$/,
  ORG_STUDENT: /^MPLO-\d{4}$/,
} as const;

export function normalizeKey(raw: string) {
  return raw.replace(/\s/g, '').toUpperCase();
}

export function isPaidPrivateKey(raw: string) {
  return REGEX.PAID_PRIVATE.test(normalizeKey(raw));
}

export function isPaidOrgKey(raw: string) {
  return REGEX.PAID_ORG.test(normalizeKey(raw));
}

export function isPaidSubscriptionKey(raw: string) {
  const key = normalizeKey(raw);
  return isPaidPrivateKey(key) || isPaidOrgKey(key);
}

export function isTempOrgKey(raw: string) {
  return REGEX.TEMP_ORG.test(normalizeKey(raw));
}

export function isOrgStudentKey(raw: string) {
  return REGEX.ORG_STUDENT.test(normalizeKey(raw));
}

export function isPortalLoginKey(raw: string) {
  const key = normalizeKey(raw);
  return isPaidSubscriptionKey(key) || isTempOrgKey(key) || isOrgStudentKey(key);
}

export function isPortalEntranceKey(raw: string) {
  return isPortalLoginKey(raw);
}

export function orgCodeFromCompanyName(companyName: string) {
  const letters = companyName.replace(/[^a-zA-Z]/g, '').toUpperCase();
  return (letters + 'XXX').slice(0, 3);
}

/** Backward-compatible aliases for shared registration components. */
export const isPaidProKey = isPaidPrivateKey;
export const isOrgMenteeKey = isOrgStudentKey;
export const isProcurementDemoKey = isTempOrgKey;

export function isStudentBriefingKey(raw: string) {
  const key = normalizeKey(raw);
  return isPaidPrivateKey(key) || isOrgStudentKey(key);
}

export function isOrgAdminKey(raw: string) {
  const key = normalizeKey(raw);
  return isPaidOrgKey(key) || isTempOrgKey(key);
}

/** Master sandbox student — André test profile. */
export function isMasterTestKey(raw: string) {
  const key = normalizeKey(raw);
  return Boolean(MASTER_TEST_KEY) && key === MASTER_TEST_KEY;
}

/** MPL-10 workspace metadata — student classroom keys (MPL-#### / MPLO-#### / master test). */
export function resolveWorkspaceLicenseKey(raw: string): string | null {
  const key = normalizeKey(raw);
  if (isMasterTestKey(key)) return key;
  return isStudentBriefingKey(key) ? key : null;
}

export function isWorkspaceLicenseKey(raw: string): boolean {
  return resolveWorkspaceLicenseKey(raw) !== null;
}

function randomDigits4() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

export function mintPaidPrivateKey() {
  return `MPL-${randomDigits4()}`;
}

export function mintTempOrgKey() {
  return `MLPT-${randomDigits4()}`;
}

/** Master Admin Tab 5 — pre-authorized validation marker minting. */
export function mintDemoValidationKey(type: 'private' | 'organisation') {
  return type === 'private' ? mintPaidPrivateKey() : mintTempOrgKey();
}

/** @deprecated Use mintDemoValidationKey */
export function mintDemoCreationCode(type: 'PRO' | 'ELT') {
  return mintDemoValidationKey(type === 'PRO' ? 'private' : 'organisation');
}
